// src/pages/gameweeks/GameweekForm.tsx
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  Gameweek,
  CreateGameweekRequest,
  UpdateGameweekRequest,
  Phase,
} from "@/types/api.types";

interface GameweekFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gameweek: Partial<Gameweek> | null;
  isEditing: boolean;
  onSave: (data: CreateGameweekRequest | UpdateGameweekRequest) => void;
  isSaving: boolean;
}

const PHASES: { value: Phase; label: string }[] = [
  { value: "GROUP_STAGE", label: "Group Stage" },
  { value: "ROUND_OF_32", label: "Round of 32" },
  { value: "ROUND_OF_16", label: "Round of 16" },
  { value: "QUARTER_FINAL", label: "Quarter Final" },
  { value: "SEMI_FINAL", label: "Semi Final" },
  { value: "THIRD_PLACE", label: "Third Place Match" },
  { value: "FINAL", label: "Final" },
];

// FIFA 2026 preset gameweeks
const PRESETS = [
  { name: "Matchweek 1", phase: "GROUP_STAGE" as Phase, roundNumber: 1 },
  { name: "Matchweek 2", phase: "GROUP_STAGE" as Phase, roundNumber: 2 },
  { name: "Matchweek 3", phase: "GROUP_STAGE" as Phase, roundNumber: 3 },
  { name: "Round of 32", phase: "ROUND_OF_32" as Phase, roundNumber: 1 },
  { name: "Round of 16", phase: "ROUND_OF_16" as Phase, roundNumber: 1 },
  { name: "Quarter Finals", phase: "QUARTER_FINAL" as Phase, roundNumber: 1 },
  { name: "Semi Finals", phase: "SEMI_FINAL" as Phase, roundNumber: 1 },
  { name: "Third Place & Final", phase: "FINAL" as Phase, roundNumber: 1 },
];

const toDatetimeLocal = (isoString?: string) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

/**
 * <input type="datetime-local"> returns a naive local-clock string with no
 * timezone (e.g. "2026-06-15T12:00"). Sending that to the backend gets it
 * stored as UTC, which then reads back at the wrong local time
 * (12:00 NPT round-tripped becomes 17:45 NPT). Convert to a real UTC ISO
 * before submit so the round-trip is timezone-correct.
 */
const localDatetimeToIso = (localValue?: string) => {
  if (!localValue) return localValue;
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return localValue;
  return date.toISOString();
};

export default function GameweekForm({
  open,
  onOpenChange,
  gameweek,
  isEditing,
  onSave,
  isSaving,
}: GameweekFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateGameweekRequest>({
    defaultValues: {
      name: "",
      phase: "GROUP_STAGE",
      roundNumber: 1,
      deadline: "",
    },
  });

  useEffect(() => {
    if (gameweek && isEditing) {
      reset({
        name: gameweek.name ?? "",
        phase: gameweek.phase ?? "GROUP_STAGE",
        roundNumber: gameweek.roundNumber ?? 1,
        deadline: toDatetimeLocal(gameweek.deadline),
      });
    } else {
      reset({
        name: "",
        phase: "GROUP_STAGE",
        roundNumber: 1,
        deadline: "",
      });
    }
  }, [gameweek, isEditing, reset]);

  const applyPreset = (preset: (typeof PRESETS)[0]) => {
    setValue("name", preset.name);
    setValue("phase", preset.phase);
    setValue("roundNumber", preset.roundNumber);
  };

  const submitHandler = (data: CreateGameweekRequest) => {
    onSave({
      ...data,
      deadline: localDatetimeToIso(data.deadline) ?? data.deadline,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-900">
            {isEditing ? "Edit Gameweek" : "Create Gameweek"}
          </DialogTitle>
        </DialogHeader>

        {/* Presets — only show when creating */}
        {!isEditing && (
          <div className="space-y-2">
            <Label className="text-gray-500 text-xs uppercase tracking-wide">
              Quick Presets
            </Label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={`${preset.phase}_${preset.roundNumber}`}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition"
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(submitHandler)} className="space-y-4 mt-2">
          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-gray-700">
              Gameweek Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              {...register("name", { required: "Name is required" })}
              placeholder="e.g. Matchweek 1"
              className="bg-white border-gray-300 mt-1"
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Phase + Round Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700">
                Phase <span className="text-red-500">*</span>
              </Label>
              {/* Disable phase/round when editing */}
              <Select
                value={watch("phase") || "GROUP_STAGE"}
                onValueChange={(val) => setValue("phase", val as Phase)}
                disabled={isEditing}
              >
                <SelectTrigger className="bg-white border-gray-300 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PHASES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isEditing && (
                <p className="text-xs text-gray-400 mt-1">
                  Phase cannot be changed after creation
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="roundNumber" className="text-gray-700">
                Round Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="roundNumber"
                type="number"
                min={1}
                max={3}
                {...register("roundNumber", {
                  required: "Round number is required",
                  min: { value: 1, message: "Minimum 1" },
                  valueAsNumber: true,
                })}
                disabled={isEditing}
                className="bg-white border-gray-300 mt-1"
              />
              {errors.roundNumber && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.roundNumber.message}
                </p>
              )}
            </div>
          </div>

          {/* Deadline */}
          <div>
            <Label htmlFor="deadline" className="text-gray-700">
              Deadline <span className="text-red-500">*</span>
            </Label>
            <Input
              id="deadline"
              type="datetime-local"
              {...register("deadline", { required: "Deadline is required" })}
              className="bg-white border-gray-300 mt-1"
            />
            {errors.deadline && (
              <p className="text-sm text-red-500 mt-1">
                {errors.deadline.message}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Team selection closes at this time. Auto-locked by scheduler.
            </p>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving
                ? "Saving..."
                : isEditing
                  ? "Update Gameweek"
                  : "Create Gameweek"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
